/**
 * Send feature — pure formatting helpers (wei → display units, gas fee math).
 * Extracted verbatim from the legacy SendPage so the new step screens and the
 * review screen share one source of truth. Policy mirrors crossy-sdk-js
 * `docs/06-gas-fee.md`.
 */

import type { GasEstimate } from "../../wallet-portfolio/types";

/**
 * Wei → token-unit string, truncated to `maxDp` decimals (floor), trailing
 * zeros and a dangling "." removed.
 */
export function formatTokenUnits(
  value: bigint,
  decimals: number,
  maxDp = 10,
): string {
  if (decimals <= 0) return value.toString();
  const unit = 10n ** BigInt(decimals);
  const whole = value / unit;
  const fraction = value % unit;
  if (fraction === 0n) return whole.toString();
  const fractionPadded = fraction.toString().padStart(decimals, "0");
  let trimmed = fractionPadded.slice(0, Math.max(0, Math.min(maxDp, decimals)));
  trimmed = trimmed.replace(/0+$/, "");
  return trimmed ? `${whole.toString()}.${trimmed}` : whole.toString();
}

/** Wei → native-coin amount: floor at 6dp, trim trailing zeros. */
export function formatWeiToNative(wei: bigint, decimals: number): string {
  return formatTokenUnits(wei, decimals, 6);
}

/** Wei → Gwei: floor at 2dp, trim trailing zeros (per-gas-unit prices). */
export function formatWeiToGwei(wei: bigint): string {
  return formatTokenUnits(wei, 9, 2);
}

/** Gas limit → comma-grouped integer (21000 → "21,000"). */
export function formatGasLimit(gasLimit: bigint): string {
  return gasLimit.toLocaleString("en-US");
}

export type GasMode = "dynamic" | "legacy";

export function isBscLegacyChainId(chainId: number | undefined): boolean {
  return chainId === 56 || chainId === 97;
}

export function getGasPriceWei(estimate: GasEstimate): bigint | undefined {
  return estimate.gasPrice ?? estimate.maxFeePerGas;
}

/** Dynamic (EIP-1559) when `maxFeePerGas` present, else Legacy. */
export function getGasMode(
  estimate: GasEstimate,
  forceLegacy = false,
): GasMode | null {
  if (forceLegacy) return getGasPriceWei(estimate) !== undefined ? "legacy" : null;
  if (estimate.maxFeePerGas !== undefined) return "dynamic";
  if (estimate.gasPrice !== undefined) return "legacy";
  return null;
}

/** Est. Tx Fee = gasLimit × (maxFeePerGas | gasPrice), in wei. */
export function getEstTxFeeWei(
  estimate: GasEstimate,
  forceLegacy = false,
): bigint | null {
  if (forceLegacy) {
    const gasPrice = getGasPriceWei(estimate);
    return gasPrice !== undefined ? estimate.gasLimit * gasPrice : null;
  }
  if (estimate.maxFeePerGas !== undefined)
    return estimate.gasLimit * estimate.maxFeePerGas;
  if (estimate.gasPrice !== undefined) return estimate.gasLimit * estimate.gasPrice;
  return null;
}

/** Max Gas = gasLimit × maxFeePerGas (dynamic) — null for legacy. */
export function getMaxGasWei(estimate: GasEstimate): bigint | null {
  if (estimate.maxFeePerGas !== undefined)
    return estimate.gasLimit * estimate.maxFeePerGas;
  return null;
}

/** Format a USD value: `$1,234.56`. Returns `$0.00` for 0 / invalid. */
export function formatUsd(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "$0.00";
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
